import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { dashboardAPI, videosAPI, getAssetUrl } from "../lib/api";
import { formatNumber } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Film,
  Clock,
  CheckCircle,
  TrendingUp,
  Eye,
  Heart,
  MessageCircle,
  Plus,
  ArrowRight,
  Sparkles,
} from "lucide-react";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentVideos, setRecentVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    const fetchData = async (retryCount = 0) => {
      const maxRetries = 3;
      
      try {
        // Fetch stats and videos separately with individual error handling
        // This prevents one failing API from blocking the other
        let statsData = null;
        let videosData = null;
        
        try {
          const statsRes = await dashboardAPI.getStats();
          if (statsRes?.data) {
            statsData = statsRes.data;
          }
        } catch (statsError) {
          console.warn("[DASHBOARD] Stats fetch failed, using defaults:", statsError.message);
          // Provide default stats so dashboard renders
          statsData = {
            total_videos: 0,
            total_views: 0,
            total_duration: 0,
            videos_this_month: 0
          };
        }
        
        try {
          const videosRes = await videosAPI.getVideos();
          if (videosRes?.data) {
            videosData = videosRes.data.slice(0, 4);
          }
        } catch (videosError) {
          console.warn("[DASHBOARD] Videos fetch failed:", videosError.message);
          videosData = [];
        }
        
        setStats(statsData);
        setRecentVideos(videosData || []);
        setFetchError(false);
        
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
        console.error("Error details:", error.response?.status, error.response?.data);
        
        // Retry on network errors or timeouts
        if (retryCount < maxRetries && (
          error.code === 'ERR_NETWORK' || 
          error.message?.includes('timeout')
        )) {
          console.log(`[DASHBOARD] Retrying fetch (attempt ${retryCount + 2}/${maxRetries + 1})...`);
          setTimeout(() => fetchData(retryCount + 1), 2000 * (retryCount + 1)); // Exponential backoff
          return;
        }
        
        // Set error state but provide default data so dashboard renders
        setFetchError(true);
        if (!stats) {
          setStats({
            total_videos: 0,
            pending_videos: 0,
            approved_videos: 0,
            published_videos: 0,
            total_views: 0,
            total_likes: 0,
            total_comments: 0,
            total_duration: 0,
            videos_this_month: 0
          });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Clickable stat cards with navigation
  const statCards = stats
    ? [
        {
          title: "Total Videos",
          value: stats.total_videos,
          icon: Film,
          color: "#00F0FF",
          bgColor: "bg-[#00F0FF]/10",
          link: "/library",
          description: "View all videos",
        },
        {
          title: "Pending Review",
          value: stats.pending_videos,
          icon: Clock,
          color: "#FF0055",
          bgColor: "bg-[#FF0055]/10",
          link: "/review",
          description: "Review pending videos",
        },
        {
          title: "Approved",
          value: stats.approved_videos,
          icon: CheckCircle,
          color: "#00FF94",
          bgColor: "bg-[#00FF94]/10",
          link: "/library?status=approved",
          description: "View approved videos",
        },
        {
          title: "Published",
          value: stats.published_videos,
          icon: TrendingUp,
          color: "#A855F7",
          bgColor: "bg-[#A855F7]/10",
          link: "/library?status=published",
          description: "View published videos",
        },
      ]
    : [];

  const engagementStats = stats
    ? [
        { label: "Total Views", value: stats.total_views, icon: Eye, link: "/engagement" },
        { label: "Total Likes", value: stats.total_likes, icon: Heart, link: "/engagement" },
        { label: "Total Comments", value: stats.total_comments, icon: MessageCircle, link: "/engagement" },
      ]
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show retry UI if data failed to load
  if (fetchError && !stats) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-muted-foreground">Failed to load dashboard data</p>
        <Button 
          onClick={() => window.location.reload()} 
          className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B]"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
      data-testid="dashboard-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back! Here&apos;s your content overview.</p>
        </div>
        <Link to="/create">
          <Button className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B] font-bold rounded-xl gap-2 neon-glow" data-testid="create-video-btn">
            <Plus className="w-5 h-5" />
            Create Video
          </Button>
        </Link>
      </div>

      {/* Stats Grid - Bento Style - CLICKABLE */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <motion.div key={stat.title} variants={itemVariants}>
            <Card 
              className="bg-card border-border/50 card-hover cursor-pointer group transition-all hover:border-[#00F0FF]/30"
              onClick={() => navigate(stat.link)}
              data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.title}</p>
                    <p className="text-3xl font-bold text-white mt-2">{stat.value}</p>
                    <p className="text-xs text-muted-foreground mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {stat.description} →
                    </p>
                  </div>
                  <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Engagement Overview - CLICKABLE */}
        <motion.div variants={itemVariants} className="lg:col-span-4">
          <Card 
            className="bg-card border-border/50 h-full cursor-pointer hover:border-[#00F0FF]/30 transition-all"
            onClick={() => navigate("/engagement")}
            data-testid="engagement-overview-card"
          >
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2 justify-between">
                <span className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#00F0FF]" />
                  Engagement Overview
                </span>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {engagementStats.map((stat) => (
                <div key={stat.label} className="flex items-center justify-between group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center group-hover:bg-[#00F0FF]/10 transition-colors">
                      <stat.icon className="w-5 h-5 text-muted-foreground group-hover:text-[#00F0FF] transition-colors" />
                    </div>
                    <span className="text-muted-foreground">{stat.label}</span>
                  </div>
                  <span className="text-xl font-bold text-white">{formatNumber(stat.value)}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Videos */}
        <motion.div variants={itemVariants} className="lg:col-span-8">
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-white">Recent Videos</CardTitle>
              <Link to="/library">
                <Button variant="ghost" size="sm" className="text-[#00F0FF] hover:text-[#00F0FF]/80 gap-1">
                  View All <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentVideos.length === 0 ? (
                <div className="text-center py-12">
                  <Film className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No videos yet. Create your first one!</p>
                  <Link to="/create">
                    <Button className="mt-4 bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B] font-bold rounded-xl">
                      Create Video
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {recentVideos.map((video) => (
                    <Link
                      key={video.id}
                      to={`/library?video=${video.id}`}
                      className="flex gap-4 p-4 bg-secondary/30 rounded-xl border border-border/30 hover:border-[#00F0FF]/30 hover:bg-secondary/50 transition-all cursor-pointer"
                      data-testid={`dashboard-video-${video.id}`}
                    >
                      <div className="w-24 h-16 bg-secondary rounded-lg overflow-hidden flex-shrink-0">
                        {video.thumbnail_url ? (
                          <img
                            src={getAssetUrl(video.thumbnail_url)}
                            alt={video.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Film className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-white truncate">{video.title}</h4>
                        <Badge
                          variant="outline"
                          className={`mt-2 text-xs ${
                            video.status === "pending"
                              ? "badge-trending"
                              : video.status === "approved"
                              ? "badge-success"
                              : video.status === "rejected"
                              ? "badge-viral"
                              : "bg-muted"
                          }`}
                        >
                          {video.status}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div variants={itemVariants}>
        <Card className="bg-gradient-to-r from-[#00F0FF]/10 to-[#FF0055]/10 border-border/50">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Ready to go viral?</h3>
              <p className="text-muted-foreground mt-1">
                Check out the latest trends and create content that resonates.
              </p>
            </div>
            <Link to="/trends">
              <Button className="bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl gap-2 border border-white/20">
                <TrendingUp className="w-5 h-5" />
                Explore Trends
              </Button>
            </Link>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
